import { PageHero, CTASection, SectionLabel } from "@/components/Section";
import Reveal from "@/components/Reveal";
import {
  Compass,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Calendar,
} from "lucide-react";

const cascade = [
  {
    n: "01",
    icon: Compass,
    horizon: "3–5 Years",
    title: "Vision",
    body: "Where the business is heading — long-term direction, market position, ownership goals.",
  },
  {
    n: "02",
    icon: CalendarRange,
    horizon: "Annual",
    title: "Priorities",
    body: "The 3–5 things that must move this year for the vision to stay on track.",
  },
  {
    n: "03",
    icon: CalendarDays,
    horizon: "Quarterly",
    title: "Objectives",
    body: "90-day blocks of measurable work — every leader owns their objectives.",
  },
  {
    n: "04",
    icon: CalendarClock,
    horizon: "Monthly",
    title: "Initiatives",
    body: "Reporting rhythm, financial reviews, progress against objectives.",
  },
  {
    n: "05",
    icon: Calendar,
    horizon: "Weekly",
    title: "Accountability",
    body: "Team stand-ups, decisions, unblockers. Where structure produces execution.",
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <PageHero
        eyebrow="How It Works"
        title="Structure that creates long-term value."
        intro="Every business is different, so we don't force a fixed operating rhythm. Instead, we build a management framework that fits your business — while creating accountability at every level."
        testId="how-it-works-hero"
      />

      {/* THE CASCADE — Vision → Weekly execution */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="how-it-works-cascade-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12 lg:mb-16">
            <div className="lg:col-span-7">
              <Reveal>
                <SectionLabel>The Framework</SectionLabel>
                <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-[#0A192F]">
                  From vision to
                  <br />
                  <span className="text-[#F68712]">weekly accountability.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 space-y-4 text-base lg:text-lg text-[#475569] leading-relaxed">
              <p>
                Everything starts with a clear long-term direction. We define where the business
                is heading over the next 3–5 years, then break that vision into annual priorities,
                quarterly objectives, monthly initiatives and weekly execution.
              </p>
              <p className="text-[#0A192F] font-semibold">
                Each level has clear ownership, measurable outcomes and regular review points.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#E2E8F0]">
            {cascade.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.n} delay={i * 100}>
                  <div
                    className="bb-card relative border-r border-b border-[#E2E8F0] p-7 lg:p-8 min-h-[320px] h-full flex flex-col"
                    data-testid={`cascade-step-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs tracking-widest text-[#F68712] font-bold">
                        {s.n}
                      </span>
                      <Icon size={22} strokeWidth={1.5} className="text-[#0A192F]" />
                    </div>
                    <div className="mt-8 font-mono text-[10px] uppercase tracking-widest text-[#475569] font-bold">
                      {s.horizon}
                    </div>
                    <h3 className="mt-1 text-2xl lg:text-3xl font-extrabold tracking-tight text-[#0A192F] leading-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-sm lg:text-base text-[#475569] leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="mt-12 lg:mt-16 max-w-3xl text-lg lg:text-xl text-[#0A192F] font-semibold leading-relaxed">
            Strategy doesn't sit in a document — it becomes part of how the business operates.
          </p>
        </div>
      </section>

      {/* THE OUTCOME */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="how-it-works-outcome-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel color="navy">The Outcome</SectionLabel>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
                A business with clear direction — and accountability built in.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[#0A192F]/15 space-y-5 text-base lg:text-lg text-[#475569] leading-relaxed">
            <p>
              The result is a business with clear direction, disciplined reporting, better
              decision-making and accountability built into the way it runs.
            </p>
            <p>
              As the business grows, the framework evolves with it — ensuring the structure that
              supports success today continues to support success tomorrow.
            </p>
            <p className="text-[#0A192F] font-semibold text-xl lg:text-2xl pt-4 border-t border-[#0A192F]/10">
              Not a project. A permanent lift in how the business is run.
            </p>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
